import React, { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Globe, CheckCircle2, ExternalLink, ArrowLeft, ShieldCheck, Info, Share2, UserCheck, CreditCard, Users2, Sparkles, MessageSquare, CalendarClock, Mail, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import { connectionMeta, linkMeta, localize, labelFor, languageLabel, countryLabel, toExternalUrl, professionCategoryLabel, specialtyLabel, POPULATIONS, APPROACHES, PAYMENT_METHODS, PROFESSIONS, WORKS_WITH, ENGAGEMENT_TYPES, professionConfig } from "../lib/meta";
import { useLanguageLabeler } from "../lib/taxonomy";
import { ContactForm } from "../components/ContactForm";
import { BookAppointment } from "../components/BookAppointment";
import { OpportunityInquiryDialog } from "../components/OpportunityInquiryDialog";
import { Button } from "../components/ui/button";

function setMeta(name, content, attr = "property") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function PsychologistProfile() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, lang } = useLang();
  const langLabel = useLanguageLabeler();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [inqOpen, setInqOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    api.get(`/psychologists/${id}`).then((r) => setP(r.data)).catch(() => setP(false)).finally(() => setLoading(false));
    api.get(`/psychologists/${id}/booking`).then((r) => setBooking(r.data)).catch(() => setBooking(null));
  }, [id]);

  useEffect(() => {
    if (booking?.bookable && searchParams.get("book") === "1") setBookOpen(true);
  }, [booking, searchParams]);

  useEffect(() => {
    if (!p) return;
    const title = `${p.full_name}${p.title ? " — " + p.title : ""} · ConQuién Hoy`;
    const desc = (p.bio || t("directory.subtitle")).slice(0, 160);
    document.title = title;
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:type", "profile");
    setMeta("og:url", window.location.href);
    setMeta("description", desc, "name");
    const photo = fileUrl(p.photo_path);
    if (photo) setMeta("og:image", photo);
    return () => { document.title = "ConQuién Hoy"; };
  }, [p, t]);

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">{t("common.loading")}</div>;
  if (!p) return (
    <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="psych-not-found">
      <h1 className="font-display text-2xl font-bold text-slateink">{t("profile.not_available_title")}</h1>
      <p className="mt-3 text-muted-foreground">{t("profile.not_available_desc")}</p>
      <Button asChild className="mt-8 rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="back-to-directory">
        <Link to="/profesionales"><ArrowLeft className="mr-1.5 h-4 w-4" />{t("profile.back_to_directory")}</Link>
      </Button>
    </div>
  );

  const photo = fileUrl(p.photo_path);
  const ALLOWED_CONN = ["email", "phone", "website", "booking", "video"];
  const conns = (p.connection_methods || []).filter((c) => ALLOWED_CONN.includes(c.type));
  const emailConn = conns.find((c) => c.type === "email");
  const phoneConn = conns.find((c) => c.type === "phone"); // backend hides this unless the pro opted into public display
  const externalConns = conns.filter((c) => ["website", "booking", "video"].includes(c.type));
  const connLabel = (type) => {
    const map = {
      website: { es: "Sitio web", en: "Website", am: "ድረ-ገጽ" },
      video: { es: "Videollamada", en: "Video call", am: "ቪዲዮ ጥሪ" },
      booking: { es: "Reservas", en: "Booking", am: "ቀጠሮ" },
      phone: { es: "Teléfono", en: "Phone", am: "ስልክ" },
      email: { es: "Email", en: "Email", am: "ኢሜይል" },
    };
    const e = map[type];
    return e ? labelFor(e, lang) : connectionMeta(type).label;
  };
  const bookingCta = booking?.booking_enabled && booking?.bookable && (booking?.appointment_types?.length || 0) > 0;
  const hasAnyConnection = bookingCta || emailConn || externalConns.length > 0 || phoneConn;
  const formatLabel = p.service_format === "online" ? t("common.online") : p.service_format === "in_person" ? t("common.in_person") : t("common.both");
  const typeLabel = p.service_type === "paid" ? t("common.paid") : p.service_type === "volunteer" ? t("common.volunteer") : t("common.both");

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: p.full_name, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success(t("common.link_copied"));
    } catch {
      try { await navigator.clipboard.writeText(url); toast.success(t("common.link_copied")); } catch { /* noop */ }
    }
  };

  const Card = ({ title, children, testId, icon: Icon }) => (
    <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid={testId}>
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold tracking-tight text-slateink">
        {Icon && <Icon className="h-4 w-4 text-terracotta" />}{title}
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  );

  const payments = (p.payment_methods || []).map((v) => localize(PAYMENT_METHODS, v, lang));
  const populations = (p.populations_served || []).map((v) => localize(POPULATIONS, v, lang));
  const approachLabel = p.approach ? localize(APPROACHES, p.approach, lang) : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/profesionales" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-terracotta" data-testid="back-to-directory">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </Link>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Identity — full width, first on all breakpoints */}
        <section className="order-1 rounded-2xl border border-border bg-white p-5 sm:p-8 lg:col-span-2" data-testid="profile-identity">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:gap-5">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-secondary">
                {photo ? <img src={photo} alt={p.full_name} className="h-full w-full object-cover" />
                  : <div className="flex h-full w-full items-center justify-center font-display text-3xl font-bold text-slateink/30">{(p.full_name || "?").charAt(0)}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="min-w-0 break-words font-display text-xl font-bold tracking-tight text-slateink sm:text-2xl">{p.full_name}</h1>
                  {p.status === "verified" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EBF6F0] px-3 py-1 text-xs font-semibold text-[#1E6B47]" data-testid="verified-badge">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t("status.verified")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground" data-testid="unverified-badge">
                      {t("profile.not_verified")}
                    </span>
                  )}
                </div>
                {p.profession && p.profession_group && (
                  <p className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground" data-testid="profile-category">{professionCategoryLabel(p.profession_group, lang)}</p>
                )}
                {p.profession && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span data-testid="profession-badge" className="max-w-full break-words rounded-full bg-slateink px-3 py-1 text-xs font-semibold text-white">
                      {(p.profession === "other_clinical" || p.profession === "other_non_clinical") && p.profession_other ? p.profession_other : localize(PROFESSIONS, p.profession, lang)}
                    </span>
                    {p.profession_category !== "non_clinical" && <span className="text-xs text-muted-foreground">{t("profile.clinical")}</span>}
                  </div>
                )}
                {p.title && <p className="mt-1 text-muted-foreground">{p.title}</p>}
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                  {(p.city || p.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{[p.city, countryLabel(p.country, lang)].filter(Boolean).join(", ")}</span>}
                  {p.languages?.length > 0 && <span className="inline-flex items-center gap-1"><Globe className="h-4 w-4" />{p.languages.map((l) => langLabel(l, lang)).join(" · ")}</span>}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {booking?.booking_enabled && booking?.status && (
                    <span data-testid="booking-status-badge" className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${booking.status === "accepting" ? "bg-[#EBF6F0] text-[#1E6B47]" : booking.status === "limited" ? "bg-[#FBF3E7] text-[#9A6B1E]" : "bg-secondary text-muted-foreground"}`}>
                      <CalendarClock className="h-3.5 w-3.5" /> {t(`booking.status_${booking.status}`)}
                    </span>
                  )}
                  {!booking?.booking_enabled && p.accepting_new_clients && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EBF6F0] px-3 py-1 text-xs font-semibold text-[#1E6B47]" data-testid="accepting-badge">
                      <UserCheck className="h-3.5 w-3.5" /> {t("profile.accepting_new")}
                    </span>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={share} className="rounded-full" data-testid="share-profile-btn">
                    <Share2 className="mr-1.5 h-3.5 w-3.5" /> {t("common.share")}
                  </Button>
                </div>
              </div>
            </div>
        </section>

        <div className="order-3 space-y-6 lg:order-2">
          <Card title={t("profile.about")} testId="profile-about">
            <p className="whitespace-pre-line text-sm leading-relaxed text-slateink/80">{p.bio || t("profile.no_bio")}</p>
          </Card>

          {p.specialties?.length > 0 && (
            <Card title={t("profile.specialties")} testId="profile-specialties" icon={Sparkles}>
              <div className="flex flex-wrap gap-2">
                {p.specialties.map((s, i) => <span key={i} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground" data-testid={`profile-specialty-${i}`}>{specialtyLabel(s, lang)}</span>)}
              </div>
            </Card>
          )}

          {p.profession_category === "non_clinical" && (() => {
            const c = professionConfig(p.profession);
            const dd = p.profession_details || {};
            const L = (o) => labelFor(o, lang);
            const loc = (opts, vals) => (vals || []).map((v) => localize(opts, v, lang));
            const TagList = ({ items }) => <div className="flex flex-wrap gap-2">{items.map((x) => <span key={x} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground">{x}</span>)}</div>;
            return (
              <>
                {c.expertise && (dd.expertise || []).length > 0 && <Card title={L(c.expertise)} testId="profile-expertise" icon={Sparkles}><TagList items={loc(c.expertise.options, dd.expertise)} /></Card>}
                {c.languagesTaught && p.service_languages?.length > 0 && <Card title={t("profile.languages_taught")} testId="profile-languages-taught" icon={Globe}><TagList items={p.service_languages.map((l) => langLabel(l, lang))} /></Card>}
                {c.who && (dd.who || []).length > 0 && <Card title={L(c.who)} testId="profile-who" icon={Users2}><TagList items={loc(c.who.options, dd.who)} /></Card>}
                {c.services && (dd.services || []).length > 0 && <Card title={L(c.services)} testId="profile-services" icon={Users2}><TagList items={loc(c.services.options, dd.services)} /></Card>}
                {(c.extras || []).map((ex) => (dd[ex.key] || []).length > 0 && <Card key={ex.key} title={L(ex)} testId={`profile-detail-${ex.key}`}><TagList items={loc(ex.options, dd[ex.key])} /></Card>)}
              </>
            );
          })()}

          {p.areas?.length > 0 && (
            <Card title={t("profile.areas")} testId="profile-areas">
              <div className="flex flex-wrap gap-2">
                {p.areas.map((a) => <span key={a} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground">{a}</span>)}
              </div>
            </Card>
          )}

          {populations.length > 0 && (
            <Card title={t("profile.populations")} testId="profile-populations" icon={Users2}>
              <div className="flex flex-wrap gap-2">
                {populations.map((a) => <span key={a} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground">{a}</span>)}
              </div>
            </Card>
          )}

          {approachLabel && (
            <Card title={t("profile.approach")} testId="profile-approach" icon={Sparkles}>
              <p className="text-sm text-slateink/90">{approachLabel}</p>
            </Card>
          )}

          {(p.works_with?.length > 0 || p.engagement_types?.length > 0) && (
            <Card title={t("profile.capabilities")} testId="profile-capabilities" icon={Users2}>
              {p.works_with?.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("profile.works_with")}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.works_with.map((w) => <span key={w} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground">{localize(WORKS_WITH, w, lang)}</span>)}
                  </div>
                </div>
              )}
              {p.engagement_types?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("profile.ways_i_work")}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.engagement_types.map((e) => <span key={e} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground">{localize(ENGAGEMENT_TYPES, e, lang)}</span>)}
                  </div>
                </div>
              )}
            </Card>
          )}

          <Card title={t("profile.credentials")} testId="profile-credentials">
            <dl className="grid gap-4 sm:grid-cols-2">
              {(p.profession_category === "non_clinical"
                ? [[t("profile.qualification"), p.title], [t("profile.qualifications"), p.qualifications], [t("profile.experience"), p.years_experience != null ? `${p.years_experience} ${t("find.years")}` : null]]
                : [[t("profile.license_org"), p.license_org], [t("profile.license_country"), countryLabel(p.license_country, lang)], [t("profile.qualification"), p.title]]
              ).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{k}</dt>
                  <dd className="mt-0.5 text-sm text-slateink/90">{v || "—"}</dd>
                </div>
              ))}
            </dl>
            {p.status === "verified" ? (
              <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#1E6B47]/25 bg-[#EBF6F0] px-4 py-3.5" data-testid="verified-callout">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#1E6B47]" />
                <div>
                  <p className="font-display text-sm font-semibold text-[#1E6B47]">{t("profile.verified_note")}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-[#1E6B47]/80">{t("profile.verified_desc")}</p>
                </div>
              </div>
            ) : (
              <p className="mt-5 flex items-start gap-2 rounded-lg bg-cream px-3 py-2.5 text-xs text-muted-foreground" data-testid="not-verified-note">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t("profile.not_verified_note")}
              </p>
            )}
          </Card>

          <div className="grid gap-6 sm:grid-cols-2">
            <Card title={t("profile.service_format")} testId="profile-format"><p className="text-sm text-slateink/90">{formatLabel}</p></Card>
            <Card title={t("profile.service_type")} testId="profile-type"><p className="text-sm text-slateink/90">{typeLabel}</p></Card>
          </div>

          {(payments.length > 0 || (p.profession_category !== "non_clinical" && p.works_with_insurance)) && (
            <Card title={p.profession_category !== "non_clinical" ? `${t("profile.payment")} · ${t("profile.insurance")}` : t("profile.payment")} testId="profile-payment" icon={CreditCard}>
              {payments.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("profile.payment")}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {payments.map((a) => <span key={a} className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium text-secondary-foreground">{a}</span>)}
                  </div>
                </div>
              )}
              {p.profession_category !== "non_clinical" && (
                <div className="mt-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("profile.insurance")}</p>
                  <p className="mt-1.5 text-sm text-slateink/90">{p.works_with_insurance ? (p.insurance_accepted || t("profile.works_insurance")) : t("profile.no_insurance")}</p>
                </div>
              )}
              <p className="mt-4 flex items-start gap-2 rounded-lg bg-cream px-3 py-2.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{p.profession_category !== "non_clinical" ? t("profile.payment_note") : t("profile.payment_note_generic")}
              </p>
            </Card>
          )}

          {p.availability && (
            <Card title={t("profile.availability")} testId="profile-availability">
              <p className="whitespace-pre-line text-sm leading-relaxed text-slateink/80">{p.availability}</p>
            </Card>
          )}

          {p.professional_links?.length > 0 && (
            <Card title={t("profile.links")} testId="profile-links">
              <div className="flex flex-wrap gap-2">
                {p.professional_links.map((l, i) => {
                  const M = linkMeta(l.type);
                  return (
                    <a key={i} href={toExternalUrl(l.url)} target="_blank" rel="noopener noreferrer" data-testid={`profile-link-${i}`}
                      className="inline-flex items-center gap-2 rounded-full border border-border px-3.5 py-2 text-sm text-slateink transition-colors hover:border-terracotta/40 hover:bg-terracotta-light">
                      <M.icon className="h-4 w-4" />{M.label}<ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </a>
                  );
                })}
              </div>
            </Card>
          )}

          <Card title={t("profile.initiatives")} testId="profile-initiatives">
            <p className="text-sm text-slateink/80">{p.available_for_initiatives ? t("profile.available_initiatives") : t("profile.not_available_initiatives")}</p>
          </Card>
        </div>

        {/* Connection sidebar */}
        <aside className="order-2 space-y-6 lg:order-3 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-white p-6" data-testid="connection-panel">
            <div className="flex items-center gap-2 text-terracotta">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-widest">{t("profile.connect")}</span>
            </div>
            {bookingCta && (
              <div className="mt-4">
                <Button size="lg" onClick={() => setBookOpen(true)} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="book-appointment-cta">
                  <CalendarClock className="mr-2 h-4 w-4" />{t("profile.book_appointment")}
                </Button>
              </div>
            )}
            {emailConn && (
              <a href={emailConn.url} className="mt-4 block" data-testid="connect-email-button">
                <Button size="lg" variant="outline" className="w-full rounded-full border-terracotta text-terracotta hover:bg-terracotta hover:text-white">
                  <Mail className="mr-2 h-4 w-4" />{t("profile.email_cta")}
                </Button>
              </a>
            )}
            {(user?.role === "organizer" || user?.role === "admin") && (
              <Button size="lg" variant="outline" onClick={() => setInqOpen(true)} className="mt-4 w-full rounded-full" data-testid="invite-opportunity-cta">
                <Sparkles className="mr-2 h-4 w-4" />{t("profile.invite_opportunity")}
              </Button>
            )}
            {(externalConns.length > 0 || phoneConn) && (
              <div className="mt-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("profile.other_methods")}</p>
                <div className="mt-3 space-y-2">
                  {phoneConn && (
                    <a href={phoneConn.url} data-testid="connect-phone-button"
                      className="flex items-center justify-between rounded-xl border border-border px-4 py-2.5 text-sm text-slateink transition-colors hover:border-terracotta/40 hover:bg-terracotta-light">
                      <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4" />{connLabel("phone")}</span>
                    </a>
                  )}
                  {externalConns.map((c, i) => {
                    const M = connectionMeta(c.type);
                    return (
                      <a key={i} href={toExternalUrl(c.url)} target="_blank" rel="noopener noreferrer" data-testid={`secondary-connection-${i}`}
                        className="flex items-center justify-between rounded-xl border border-border px-4 py-2.5 text-sm text-slateink transition-colors hover:border-terracotta/40 hover:bg-terracotta-light">
                        <span className="inline-flex items-center gap-2"><M.icon className="h-4 w-4" />{connLabel(c.type)}</span>
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                      </a>
                    );
                  })}
                </div>
                {externalConns.length > 0 && <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t("profile.connect_note")}</p>}
              </div>
            )}
            {!hasAnyConnection && <p className="mt-4 text-sm text-muted-foreground">{t("common.none")}</p>}
          </div>

          {p.has_contact_form && (
            <div className="rounded-2xl border border-border bg-white p-6" data-testid="contact-form-panel">
              <div className="flex items-center gap-2 text-terracotta">
                <MessageSquare className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-widest">{t("contact.title")}</span>
              </div>
              <div className="mt-4">
                <ContactForm psychologistId={p.id} />
              </div>
            </div>
          )}
        </aside>
      </div>
      {booking && <BookAppointment open={bookOpen} onOpenChange={setBookOpen} psychologist={p} booking={booking} initialTypeId={searchParams.get("type") || undefined} />}
      <OpportunityInquiryDialog open={inqOpen} onOpenChange={setInqOpen} psychologist={p} />
    </div>
  );
}
